import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnTab from "@shared/components/SnTab";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PMS_CLS_REG_002_POP_01 from "@menu/PMS/SN_PMS_CLS_REG_002/SN_PMS_CLS_REG_002_POP_01";
import SN_PMS_CLS_REG_002_POP_02 from "@menu/PMS/SN_PMS_CLS_REG_002/SN_PMS_CLS_REG_002_POP_02";
import CommonSpinner from "@widgets/CommonSpinner";

interface Props {
  Menuprops: menuInfo;
}

/** 상단 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid1";

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_PMS_CLS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#region  Components useState
  const [loading, setLoading] = useState(false); // 비용집계 실행 중 스피너
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [tab, setTab] = useState<string>("1");
  /** 상단 버튼 메타(sys_headerctrl). getInitForm 이 이미 {controls, data} 로 내려준다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    const modifiedRowsD = getGridCurrent("grid3").getChange();
    const modifiedRowsDD = getGridCurrent("grid4").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0 ||
      modifiedRowsDD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_pms_cls_reg_002_01",
        params: {
          ...searchDataToUse,
          dt_to: searchDataToUse.dt_to?.toString().slice(0, 6),
        },
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      if (!data?.[0]?.length) {
        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: [],
        });
        return;
      }

      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  // 평가기간 변경 시 자동 조회. 기간 pop은 start/end 두 번 발화하므로 microtask로 한 번만 실행.
  const autoSearchTimer = useRef<any>(null);
  const onValueChanged = (e: any) => {
    if (e?.colId !== "dt_from" && e?.colId !== "dt_to") return;
    const d = e?.rowData ?? {};
    clearTimeout(autoSearchTimer.current);
    autoSearchTimer.current = setTimeout(() => onSearch(d), 0);
  };

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return;
    }

    const searchForm = searchRef.current?.getFormdata?.() ?? {};
    const dtToYm = normalizeYm(searchForm.dt_to);

    getGridCurrent("grid1").forEachNode((node: any) => {
      if (normalizeYm(node.data.ym) === dtToYm) {
        node.setSelected(true);
      }
    });

    getGridCurrent("grid1").deleteAllRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      return;
    }

    setDataSources((prev: any) => ({
      ...prev,
      grid2: [],
      grid3: [],
      grid4: [],
    }));

    return await onSave();
  };
  //endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();

      // L 필수값 체크
      const requiredValuesCheck = getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheck) {
        return false;
      }

      const elemamhChanges = getGridCurrent("grid1").getChange();

      let SaveData;

      if (elemamhChanges.modifiedRowcount > 0) {
        SaveData = {
          elemamh: elemamhChanges,
        };
      } else {
        const rows: any[] = [];

        getGridCurrent("grid1").forEachNode((node: any) => {
          rows.push(node.data);
        });

        SaveData = {
          elemamh: {
            insert: rows,
            update: [],
            delete: [],
            modifiedRowcount: rows.length,
          },
        };
      }

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_CLS_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  const normalizeYm = (value: any) => {
    if (value == null) return "";
    const text = String(value).replace(/\D/g, "");
    return text.length >= 6 ? text.slice(0, 6) : "";
  };

  // 집계 → 프로젝트별 노무비 총계를 pms_pjt_elemamh(노무비 예산코드)에 반영 후 재조회.
  const applySalaryAllocation = async (result: any) => {
    const alloc = result?.alloc;
    const ym = result?.ym_end;
    if (!Array.isArray(alloc) || alloc.length === 0 || !ym) return;

    // 노무비 예산코드(fg_budget='2') 조회.
    const budgetRtn: any = await fnGetDataSet({
      id: "sn_pms_cls_reg_002_05",
      params: { cd_firm: userInfo.cd_firm },
    });
    const budgets = Array.isArray(budgetRtn?.[0]) ? budgetRtn[0] : [];
    const cd_budget = budgets?.[0]?.cd_budget;
    if (!cd_budget) {
      await showDialog(
        "alter",
        "노무비 예산코드(fg_budget='2')가 없습니다. 예산코드를 확인해 주세요."
      );
      return;
    }

    const rows = alloc.map((a: any) => ({
      cd_pjt: a.cd_pjt,
      am_cost: a.am_total, // 총계(급여+기타)
    }));
    const res: any = await callApi("SN_PMS_CLS_REG_002_Alloc_Save", {
      cd_firm: userInfo.cd_firm,
      ym,
      cd_budget,
      rows,
    });
    if (res?.status) {
      await onSearch(searchRef.current?.getFormdata?.());
    }
  };

  const onBtnsClick = async (id: string) => {
    switch (id) {
      case "Aggregate": {
        // 비용집계 팝업: API 가져오기(계정별 원장) → 임시저장(cos_elemaml_if) → 비용집계(pms_pjt_elemamh 경비).
        const searchForm = searchRef.current?.getFormdata?.() ?? {};
        if (!searchForm.cd_biz || !normalizeYm(searchForm.dt_to)) {
          showDialog("alter", "사업장과 평가기간을 확인해 주세요.");
          return;
        }
        const resultAgg = await showPopupModal({
          title: "비용집계",
          children: (
            <SN_PMS_CLS_REG_002_POP_02
              selectedData={{
                cd_biz: searchForm.cd_biz,
                dt_from: searchForm.dt_from,
                dt_to: searchForm.dt_to,
              }}
            />
          ),
          width: "80%",
          height: "80vh",
          okayBtn: false,
        });
        if (resultAgg?.aggregated) {
          await onSearch(searchRef.current?.getFormdata?.());
        }
        break;
      }

      case "Salary":
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        const component = (
          <SN_PMS_CLS_REG_002_POP_01 selectedData={selectedData} />
        );
        const resultSal = await showPopupModal({
          title: "급여가져오기",
          children: component,
          okayBtnText: "집계",
        });
        if (!resultSal || resultSal === "close" || resultSal === "ok") return;
        // 집계 → 노무비배부 결과(resultSal = {cd_biz, ym_end, alloc})를 메인 노무비에 반영.
        await applySalaryAllocation(resultSal);
        break;
    }
  };

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {},
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <CommonSpinner isLoading={loading} />
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onValueChanged}
        />
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "프로젝트정보" }}
            events={{ ...grids.grid1.events, onBtnEtcClick: onBtnsClick }}
            filter={{
              refs: [
                gridRefs.current["grid2"],
                gridRefs.current["grid3"],
                gridRefs.current["grid4"],
              ],
              columns: ["cd_pjt"],
            }}
            // 비용집계 / 급여가져오기 (sys_headerctrl, cd_param 이 클릭 id)
            headerInfo={headerInfo}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            titles={["재료비", "노무비", "경비"]}
            width={"100%"}
            selectedTab={tab}
            onTabChanged={(index: any) => {
              setTab(index);
            }}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid2.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid3.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid4"]}
                columnConfig={grids.grid4.columns}
                rowData={grids.grid4.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid4.events}
              />,
            ]}
          />
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PMS_CLS_REG_002;
